<ui-split-view pageType="trust-ledger" class="bg-gray-50">
	<div leftPanel>
		<div class="mb-10 overflow-y-auto">
			<ui-content-header title="Trust Ledger" type="page">
				<div title-action class="flex items-center gap-2">
					<ui-pill
						[type]="
							trustLedgerTotals?.difference === 0
								? PILL_TYPES.BALANCED
								: PILL_TYPES.UNBALANCED
						"
					></ui-pill>
				</div>
			</ui-content-header>

			<div
				class="grid-cols-[repeat(auto-fit,minmax(500px,1fr))] grid gap-4 mt-4"
			>
				<ui-section title="Receipts" [noStyleContent]="true">
					<div title-action class="flex items-center gap-2">
						<ui-button
							preset="MEDIUM_EMPHASIS"
							id="ledger-receipts-custom"
							icon="plus"
							size="sm"
							(click)="addReceipt()"
							[testHook]="testHooks.LedgerReceiptsCustomButton"
							label="Add"
						></ui-button>
					</div>
					<div actions class="flex flex-row justify-between items-center">
						<p class="text-lg font-semibold">
							{{ trustLedgerTotals?.receiptsTotal | centsToDollars }}
						</p>
					</div>

					<div section-content>
						<div
							class="flex px-6 py-2 w-full flex-1 justify-between border-b border-gray-200 bg-red-100"
							*ngIf="trustLedgerTotals?.difference < 0"
						>
							<div
								class="flex w-full justify-between items-center text-red-500"
							>
								<p>
									Cash shortfall of
									<strong
										class="text-lg"
										[testHook]="testHooks.ShortfallAmountText"
									>
										{{
											math.abs(trustLedgerTotals.difference) | centsToDollars
										}}</strong
									>
								</p>
								<ui-button
									preset="EXTRA_LOW_EMPHASIS"
									icon="plus"
									size="sm"
									(click)="addPaymentFromClient()"
									icon="plus"
									label="Add receipt"
									[testHook]="testHooks.AddReceiptButton"
								></ui-button>
							</div>
						</div>
						<div
							*ngIf="trustLedgerReceiptItems?.length > 0; else noReceipts"
							id="receipt-list"
							cdkDropList
							cdkDragPreview
							(cdkDropListDropped)="sortReceipts($event)"
							class="relative flex flex-col divide-y divide-gray-200"
						>
							<div
								cdkDrag
								*ngFor="
									let item of trustLedgerReceiptItems;
									let i = index;
									trackBy: ledgerItemId
								"
							>
								<div
									class="flex w-full relative items-center justify-between px-6 py-3 hover:bg-grey-lightest group"
									[class.text-red]="
										item?.type === ledgerItemTypes.CASH_SHORTFALL
									"
									[id]="'adjustment' + i"
								>
									<div class="flex flex-col">
										<p class="flex-1">
											{{ trustLedgerReceiptItemDescriptions[i] }}
										</p>
										<span class="text-sm text-grey mt-1" *ngIf="item?.date"
											>Received on {{ item.date | date: 'mediumDate' }}</span
										>
									</div>

									<p class="font-medium text-lg text-gray-500">
										{{
											item?.netAmount !== undefined && item?.netAmount !== null
												? (item?.netAmount | centsToDollars)
												: (item?.amount | centsToDollars)
										}}
									</p>

									<div
										[@fade]
										class="absolute top-0 left-0 w-full h-full flex gap-5 justify-center items-center rounded-xl bg-white bg-opacity-80 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
									>
										<ui-button
											preset="EXTRA_LOW_EMPHASIS"
											icon="selector"
											cdkDragHandle
											class="cursor-grabbing"
											size="sm"
											[testHook]="testHooks.SortButton"
										></ui-button>
										<ui-button
											*ngIf="
												item?.type !== customLedgerItem &&
												item?.type !== mortgageItem &&
												item?.type !== salePriceItem
											"
											preset="EXTRA_LOW_EMPHASIS"
											icon="edit"
											class="text-green"
											(click)="editReceiptTitle(item)"
											size="sm"
											[testHook]="testHooks.EditTitleButton"
											label="Edit title"
										></ui-button>
										<ui-button
											*ngIf="item?.type === mortgageItem"
											preset="EXTRA_LOW_EMPHASIS"
											icon="edit"
											class="text-green"
											(click)="editMortgage(item)"
											size="sm"
											[testHook]="testHooks.EditDetailsButton"
											label="Edit details"
										></ui-button>
										<ui-button
											*ngIf="item?.type === salePriceItem"
											preset="EXTRA_LOW_EMPHASIS"
											icon="edit"
											class="text-green"
											(click)="editSalePriceItem(item)"
											size="sm"
											label="Edit details"
										></ui-button>
										<ui-button
											*ngIf="item?.type === customLedgerItem"
											preset="EXTRA_LOW_EMPHASIS"
											icon="edit"
											class="text-green"
											(click)="editCustomReceipt(item.id, item)"
											size="sm"
											[testHook]="testHooks.EditButton"
											label="Edit"
										></ui-button>
										<ui-button
											*ngIf="item?.type === customLedgerItem"
											preset="EXTRA_LOW_EMPHASIS"
											icon="trash"
											class="text-green"
											(click)="removeReceipt(item.id)"
											size="sm"
											[testHook]="testHooks.RemoveButton"
											label="Remove"
										></ui-button>
										<ui-button
											preset="EXTRA_LOW_EMPHASIS"
											*ngIf="
												item?.type &&
												item?.type !== ledgerItemTypes.CASH_SHORTFALL &&
												item?.type !== customLedgerItem
											"
											[matTooltip]="getAnchorText(item)"
											icon="external-link"
											(click)="goToItem(item)"
											size="sm"
											class="text-green"
											[testHook]="testHooks.GoToItemButton"
											label="View {{ getAnchorText(item) }}"
										></ui-button>
									</div>
								</div>
							</div>
						</div>
						<ng-template #noReceipts>
							<div class="flex justify-center items-center py-4">
								<p class="text-gray-500">No receipts</p>
							</div>
						</ng-template>
					</div>
				</ui-section>

				<ui-section title="Payments" [noStyleContent]="true">
					<div title-action class="flex items-center gap-2">
						<div
							id="ledger-payments-custom"
							(clickOutside)="showPaymentOptions = false"
						>
							<ui-button
								preset="MEDIUM_EMPHASIS"
								icon="plus"
								size="sm"
								(click)="showPaymentOptions = !showPaymentOptions"
								[testHook]="testHooks.AddANewLineButton"
								label="Add"
							></ui-button>
							<div class="relative align-lef">
								<app-dropdown-options
									class="right-0"
									[itemGroups]="[{ items: paymentLineOptions }]"
									[show]="showPaymentOptions"
									(onSelect)="selectAddPaymentLine($event)"
									[testHook]="testHooks.DropdownOptions"
								>
								</app-dropdown-options>
							</div>
						</div>
					</div>
					<div actions class="flex flex-row justify-between items-center">
						<p class="text-lg font-semibold">
							{{ trustLedgerTotals?.paymentsTotal | centsToDollars }}
						</p>
					</div>
					<div section-content>
						<div
							*ngIf="trustLedgerTotals?.difference > 0"
							class="flex px-6 py-2 w-full flex-1 justify-between border-b border-gray-200 bg-red-100"
						>
							<div
								class="flex w-full justify-between items-center text-red-500"
							>
								<p>
									You have a cash excess of
									<strong class="ml-3">
										{{
											math.abs(trustLedgerTotals.difference) | centsToDollars
										}}</strong
									>
								</p>
								<ui-button
									preset="EXTRA_LOW_EMPHASIS"
									size="sm"
									(click)="addPaymentToClient()"
									icon="plus"
									label="Add payment"
									[testHook]="testHooks.AddPaymentToClientButton"
								></ui-button>
							</div>
						</div>
						<div
							id="payment-list"
							cdkDropList
							cdkDragPreview
							(cdkDropListDropped)="sortPayments($event)"
							class="relative flex flex-col divide-y divide-gray-200"
						>
							<div
								cdkDrag
								*ngFor="
									let item of trustLedgerPaymentItems;
									let i = index;
									trackBy: ledgerItemId
								"
							>
								<div
									class="flex w-full relative items-center justify-between px-6 py-3 hover:bg-grey-lightest group"
									[id]="'adjustment' + i"
								>
									<div class="flex flex-col">
										<p class="flex-1">
											{{ trustLedgerPaymentItemDescriptions[i] }}
										</p>
										<span class="text-sm text-grey mt-1" *ngIf="item?.date"
											>Received on {{ item.date | date: 'mediumDate' }}</span
										>
									</div>

									<p class="font-medium text-lg text-gray-500">
										{{
											item?.netAmount !== undefined && item?.netAmount !== null
												? (item.netAmount | centsToDollars)
												: (item.amount | centsToDollars)
										}}
									</p>

									<div
										[@fade]
										class="absolute top-0 left-0 w-full h-full flex gap-5 justify-center items-center rounded-xl bg-white bg-opacity-80 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
									>
										<ui-button
											preset="EXTRA_LOW_EMPHASIS"
											icon="selector"
											cdkDragHandle
											[testHook]="testHooks.SortButton1"
											size="sm"
										></ui-button>
										<ui-button
											*ngIf="item?.type === salePriceItem"
											preset="EXTRA_LOW_EMPHASIS"
											icon="edit"
											class="text-green"
											(click)="editSalePriceItem(item)"
											size="sm"
											label="Edit details"
										></ui-button>
										<ui-button
											preset="EXTRA_LOW_EMPHASIS"
											*ngIf="
												item?.type &&
												item?.type !== ledgerItemTypes.CASH_SHORTFALL &&
												item?.type !== customLedgerItem
											"
											[matTooltip]="getAnchorText(item)"
											icon="external-link"
											(click)="goToItem(item)"
											size="sm"
											class="text-green"
											[testHook]="testHooks.GoToItemButton1"
											[label]="'View ' + getAnchorText(item)"
										></ui-button>
										<ui-button
											*ngIf="
												item?.type !== customLedgerItem &&
												item?.type !== salePriceItem
											"
											preset="EXTRA_LOW_EMPHASIS"
											icon="edit"
											class="text-green"
											(click)="editPaymentTitle(item)"
											size="sm"
											[testHook]="testHooks.EditTitleButton1"
											label="Edit title"
										></ui-button>
										<ui-button
											*ngIf="item?.type === customLedgerItem"
											preset="EXTRA_LOW_EMPHASIS"
											icon="edit"
											class="text-green"
											(click)="editCustomPayment(item.id, item)"
											size="sm"
											[testHook]="testHooks.EditButton1"
											label="Edit"
										></ui-button>
										<ui-button
											*ngIf="item?.deletable"
											preset="EXTRA_LOW_EMPHASIS"
											icon="trash"
											class="text-green"
											(click)="removePayment(item)"
											size="sm"
											[testHook]="testHooks.RemoveButton1"
											label="Remove"
										></ui-button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</ui-section>
			</div>
		</div>
	</div>

	<div rightPanel>
		<ng-container
			*ngIf="splitViewService.shouldShowRightPanel('trust-ledger')()"
		>
			<ng-container
				*ngIf="
					statementOfTrustLedgerDocSourceIdByMatterType$ | async as sourceId
				"
			>
				<app-documents-word-matter-viewer
					*ngIf="!(useHtmlDocuments$ | async)"
					sourceId="{{ sourceId }}"
					[usingSplitView]="true"
				></app-documents-word-matter-viewer>
			</ng-container>

			<div class="grid min-h-0" *ngIf="useHtmlDocuments$ | async">
				<app-document-matter-viewtor
					sourceId="tl"
					[confirmationAction]="confirmationAction"
					[disabledElements]="disabledElements"
				></app-document-matter-viewtor>
			</div>
		</ng-container>
	</div>
</ui-split-view>
